import { CollateralVehicle } from '../types'

const UNKNOWN_VEHICLE_TEXT = "Customer's preferred vehicle is unknown"
const NONE_SELECTED_TEXT = 'None Selected'

interface RawCollateral {
    asset_name?: string
    asset_data?: {
        make?: string | null
        model?: string | null
        [key: string]: unknown
    }
}

export function isVehicleNoneSelected(
    make: string | null | undefined,
    model: string | null | undefined,
): boolean {
    const isMakeEmpty = !make?.trim() || make.trim() === '-'
    const isModelEmpty = !model?.trim() || model.trim() === '-'

    return isMakeEmpty && isModelEmpty
}

/**
 * Gets vehicle display text from raw collateral data (used in leads list)
 * Checks asset_data first for make/model validation
 */
export function getVehicleRequestedFromRaw(
    collaterals?: RawCollateral[],
): string {
    const collateral = collaterals?.[0]

    if (!collateral) {
        return UNKNOWN_VEHICLE_TEXT
    }

    // Check if asset_data exists and both make and model are dash/null/empty
    const assetData = collateral.asset_data

    if (assetData && isVehicleNoneSelected(assetData.make, assetData.model)) {
        return NONE_SELECTED_TEXT
    }

    // Fallback to asset_name
    return collateral.asset_name?.length
        ? collateral.asset_name
        : UNKNOWN_VEHICLE_TEXT
}

/**
 * Gets vehicle display text from parsed CollateralVehicle (used in lead detail modal)
 * Checks make/model directly from the collateral object
 */
export function getVehicleDisplayFromCollateral(
    collateral: CollateralVehicle | string | undefined,
): string {
    if (!collateral || typeof collateral === 'string') {
        return UNKNOWN_VEHICLE_TEXT
    }

    // Check if both make and model indicate "None Selected"
    if (isVehicleNoneSelected(collateral.make, collateral.model)) {
        return NONE_SELECTED_TEXT
    }

    // Build display string from year, make, model (filter out dashes and empty values)
    const cleanValue = (value: string | number | null | undefined) => {
        const str = value?.toString()?.trim()

        return str && str !== '-' && str !== '0' ? str : null
    }

    const parts = [
        cleanValue(collateral.year),
        cleanValue(collateral.make),
        cleanValue(collateral.model)?.toUpperCase(),
    ].filter(Boolean)

    return parts.length > 0 ? parts.join(' ') : UNKNOWN_VEHICLE_TEXT
}
